import { useRef } from "react";
import type { TabDefinition, TabId } from "../types";
import { GridIcon } from "./icons";
import styles from "./TabBar.module.css";

interface TabBarProps {
  readonly tabs: TabDefinition[];
  readonly activeTab: TabId;
  readonly onTabChange: (tabId: TabId) => void;
  readonly onMoreClick: () => void;
  readonly moreActive: boolean;
}

// Chat + Actions are always pinned; up to this many extra modules can join them.
const MAX_PINNED_MODULES = 2;
const FIXED_TAB_COUNT = 2; // Chat, Actions

export function TabBar({ tabs, activeTab, onTabChange, onMoreClick, moreActive }: Readonly<TabBarProps>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const totalCount = tabs.length + 1; // +1 for "More" button
  // Reserve empty slots for unfilled pin slots so "More" stays docked at the
  // right edge instead of drifting left when fewer modules are pinned.
  const emptyPinSlots = Math.max(0, MAX_PINNED_MODULES - (tabs.length - FIXED_TAB_COUNT));

  function getTargetIndex(key: string, index: number): number | null {
    switch (key) {
      case "ArrowRight": return (index + 1) % totalCount;
      case "ArrowLeft": return (index - 1 + totalCount) % totalCount;
      case "Home": return 0;
      case "End": return totalCount - 1;
      default: return null;
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const target = getTargetIndex(e.key, index);
    if (target === null) return;
    e.preventDefault();
    tabRefs.current[target]?.focus();
  }

  return (
    <div className={styles.tabBar} role="tablist" aria-label="Main navigation">
      {tabs.map((tab, i) => {
        const active = tab.id === activeTab;
        const Icon = tab.icon;
        return (
          <button
            type="button"
            key={tab.id}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={`${styles.tab} ${active ? styles.tabActive : ""}`}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            <span className={styles.tabIcon}>
              <Icon />
              {tab.badge != null && tab.badge > 0 && (
                <span className={styles.tabBadge} aria-label={`${tab.badge} pending`}>{tab.badge}</span>
              )}
            </span>
            <span className={styles.tabLabel} title={tab.label}>{tab.label}</span>
          </button>
        );
      })}
      {Array.from({ length: emptyPinSlots }, (_, i) => (
        <div key={`empty-pin-slot-${i}`} className={styles.emptyPinSlot} aria-hidden="true" />
      ))}
      <button
        type="button"
        ref={(el) => { tabRefs.current[tabs.length] = el; }}
        role="tab"
        aria-selected={moreActive}
        tabIndex={moreActive ? 0 : -1}
        className={`${styles.tab} ${moreActive ? styles.tabActive : ""}`}
        onClick={onMoreClick}
        onKeyDown={(e) => handleKeyDown(e, tabs.length)}
      >
        <span className={styles.tabIcon}><GridIcon /></span>
        <span className={styles.tabLabel}>More</span>
      </button>
    </div>
  );
}
